import { z } from "zod";
import { draftedTestDatasetSchema } from "./analyticsDraftedTest";
import { CollabPhaseSchema, collabPhaseDisplay } from "./collabPhase";
// Annotating each transform with the shared shape makes conformance a
// property of the model rather than something a call site discovers.
import type {
  CollabDraftAnalyticsColumn,
  CollabDraftAttributeTestColumn,
  CollabDraftSupportColumn,
  CollabDraftTestingTable,
} from "./collabPlanTypes";
import { CollabMessageSchema } from "./collabMessage";

/**
 * Collaborative Control Session Types
 *
 * These types define the data structures for the collaborative AI control
 * generation flow where users can review and iterate on a draft plan before
 * generating the final control.
 */

// Phases of the collaborative session. The vocabulary lives in collabPhase.ts
// (it is not control-specific) and is re-exported here because this file is
// the controls flow's single import surface.
export { CollabPhaseSchema, PHASE_ORDER } from "./collabPhase";
export type { CollabPhase } from "./collabPhase";

// Control types matching the existing control model
export const CollabControlTypeSchema = z.enum(["bpc", "itgc", "ipe", "other"]);
export type CollabControlType = z.infer<typeof CollabControlTypeSchema>;

// Proposed attribute in the draft plan.
// `id` ties a draft item back to its persisted row: finalize matches by id, so
// dropping it turns an edit into delete-and-recreate (losing test history).
// zod strips undeclared keys, which is why it must be declared here.
export const ProposedAttributeSchema = z.object({
  id: z.string().nullable().optional(),
  reference: z.string(),
  statement: z.string(),
});
export type ProposedAttribute = z.infer<typeof ProposedAttributeSchema>;

// Key report in the draft plan. `id` ties it to its persisted `key_report` row
// (see ProposedAttributeSchema); finalize matches on it so a rename stays an
// update instead of dissociate-and-recreate.
export const KeyReportSchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string(),
  description: z.string(),
});
export type KeyReport = z.infer<typeof KeyReportSchema>;

export const DraftSupportColumnSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string(),
    description: z.string(),
    data_type: z.enum(["string", "number"]),
  })
  .transform(
    (c): CollabDraftSupportColumn => ({
      id: c.id,
      name: c.name,
      description: c.description,
      dataType: c.data_type,
    }),
  );
export type DraftSupportColumn = z.infer<typeof DraftSupportColumnSchema>;

export const DraftAttributeTestColumnSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string().nullable(),
    pass_criteria: z.string(),
    fail_criteria: z.string().nullable().optional(),
    na_criteria: z.string().nullable().optional(),
    procedure_guidance: z.string().nullable().optional(),
    tolerances: z.string().nullable().optional(),
    expected_evidences: z.string().nullable().optional(),
    attribute_reference: z.string().nullable().optional(),
  })
  .transform(
    (c): CollabDraftAttributeTestColumn => ({
      id: c.id,
      name: c.name,
      passCriteria: c.pass_criteria,
      failCriteria: c.fail_criteria,
      naCriteria: c.na_criteria,
      procedureGuidance: c.procedure_guidance,
      tolerances: c.tolerances,
      expectedEvidences: c.expected_evidences,
      attributeReference: c.attribute_reference,
    }),
  );
export type DraftAttributeTestColumn = z.infer<
  typeof DraftAttributeTestColumnSchema
>;

export const DraftAnalyticsColumnSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string(),
    attribute_reference: z.string().nullable().optional(),
    pass_criteria: z.string(),
    fail_criteria: z.string().nullable().optional(),
    na_criteria: z.string().nullable().optional(),
    procedure_guidance: z.string().nullable().optional(),
    tolerances: z.string().nullable().optional(),
    expected_evidences: z.string().nullable().optional(),
    drafted_test: z.string().nullable().optional(),
    suggested_category_id: z.number().nullable().optional(),
    suggested_category_name: z.string().nullable().optional(),
    drafted_test_datasets: z.array(draftedTestDatasetSchema).default([]),
  })
  .transform(
    (c): CollabDraftAnalyticsColumn => ({
      id: c.id,
      name: c.name,
      attributeReference: c.attribute_reference,
      passCriteria: c.pass_criteria,
      failCriteria: c.fail_criteria,
      naCriteria: c.na_criteria,
      procedureGuidance: c.procedure_guidance,
      tolerances: c.tolerances,
      expectedEvidences: c.expected_evidences,
      draftedTest: c.drafted_test,
      suggestedCategoryId: c.suggested_category_id,
      suggestedCategoryName: c.suggested_category_name,
      draftedTestDatasets: c.drafted_test_datasets,
    }),
  );
export type DraftAnalyticsColumn = z.infer<typeof DraftAnalyticsColumnSchema>;

// Procedure type
export const ProcedureTypeSchema = z.enum(["sample", "direct"]);
export type ProcedureType = z.infer<typeof ProcedureTypeSchema>;

// Workpaper example types
export const WorkpaperExampleTypeSchema = z.enum([
  "annotation_style",
  "exception_handling",
  "source_reference",
]);
export type WorkpaperExampleType = z.infer<typeof WorkpaperExampleTypeSchema>;

export const WorkpaperExampleSchema = z
  .object({
    example_type: WorkpaperExampleTypeSchema,
    value: z.string(),
  })
  .transform((e) => ({ exampleType: e.example_type, value: e.value }));
export type WorkpaperExample = z.infer<typeof WorkpaperExampleSchema>;

// Testing table in the draft plan
export const DraftTestingTableSchema = z
  .object({
    id: z.string().nullable().optional(),
    name: z.string(),
    purpose: z.string(),
    procedure_type: ProcedureTypeSchema,
    attribute_references: z.array(z.string()),
    support_columns: z.array(DraftSupportColumnSchema).default([]),
    attribute_test_columns: z.array(DraftAttributeTestColumnSchema).default([]),
    analytics_columns: z.array(DraftAnalyticsColumnSchema).default([]),
    removed_analytics_columns: z.array(DraftAnalyticsColumnSchema).default([]),
    testing_context: z.string().nullable().optional(),
    documentation_standards: z.string().nullable().optional(),
  })
  .transform(
    (t): CollabDraftTestingTable<ProcedureType> => ({
      id: t.id,
      name: t.name,
      purpose: t.purpose,
      procedureType: t.procedure_type,
      attributeReferences: t.attribute_references,
      supportColumns: t.support_columns,
      attributeTestColumns: t.attribute_test_columns,
      analyticsColumns: t.analytics_columns,
      removedAnalyticsColumns: t.removed_analytics_columns,
      testingContext: t.testing_context,
      documentationStandards: t.documentation_standards,
    }),
  );
export type DraftTestingTable = z.infer<typeof DraftTestingTableSchema>;

// Draft plan structure
export const DraftPlanSchema = z
  .object({
    control_name: z.string(),
    control_type: CollabControlTypeSchema,
    control_description: z.string(),
    proposed_attributes: z.array(ProposedAttributeSchema),
    testing_tables: z.array(DraftTestingTableSchema),
    key_reports: z.array(KeyReportSchema),
    workpaper_examples: z.array(WorkpaperExampleSchema).nullable().optional(),
    reasoning: z.string(),
    // Derived by the server on every response and never sent back: patches carry
    // tables, not the plan.
    untested_attribute_references: z.array(z.string()).default([]),
    // Session state the server's direct-table rule judges an edit by. Sent only
    // when set, and never where hybrid testing is off.
    procedure_bases_seeded: z.boolean().default(false),
    seeded_direct_table_ids: z.array(z.string()).default([]),
  })
  .transform((p) => ({
    controlName: p.control_name,
    controlType: p.control_type,
    controlDescription: p.control_description,
    proposedAttributes: p.proposed_attributes,
    testingTables: p.testing_tables,
    keyReports: p.key_reports,
    workpaperExamples: p.workpaper_examples,
    reasoning: p.reasoning,
    untestedAttributeReferences: p.untested_attribute_references,
    procedureBasesSeeded: p.procedure_bases_seeded,
    seededDirectTableIds: p.seeded_direct_table_ids,
  }));
export type DraftPlan = z.infer<typeof DraftPlanSchema>;

// Testing table input for patches (same as DraftTestingTable)
export const DraftTestingTableInputSchema = DraftTestingTableSchema;
export type DraftTestingTableInput = z.infer<
  typeof DraftTestingTableInputSchema
>;

// Plan patch for structured editing. Held in memory camelCase like everything
// else; `planPatchToRequestBody` puts it back on the wire.
export const PlanPatchSchema = z.object({
  controlName: z.string().optional(),
  controlDescription: z.string().optional(),
  controlType: CollabControlTypeSchema.optional(),
  addAttributes: z.array(ProposedAttributeSchema).optional(),
  // Matched id-first (with a display-key fallback), so an edit stays an edit even
  // when the attribute code is re-lettered: apply_patch finds the row by id and
  // rewrites its reference in place, keeping the id — and therefore the row and its
  // test history — instead of dropping and recreating it.
  updateAttributes: z.array(ProposedAttributeSchema).optional(),
  removeAttributeRefs: z.array(z.string()).optional(),
  addTestingTables: z.array(DraftTestingTableInputSchema).optional(),
  removeTestingTableNames: z.array(z.string()).optional(),
  updateTestingTables: z.array(DraftTestingTableInputSchema).optional(),
  addKeyReports: z.array(KeyReportSchema).optional(),
  removeKeyReportNames: z.array(z.string()).optional(),
  workpaperExamples: z.array(WorkpaperExampleSchema).optional(),
});
export type PlanPatch = z.infer<typeof PlanPatchSchema>;

// Back onto the wire. The controls collab router answers in snake_case and
// takes snake_case request bodies, so every camelCase field the UI holds is
// renamed exactly once, here.
const analyticsColumnToRequest = (c: DraftAnalyticsColumn) => ({
  id: c.id ?? null,
  name: c.name,
  attribute_reference: c.attributeReference ?? null,
  pass_criteria: c.passCriteria,
  fail_criteria: c.failCriteria ?? null,
  na_criteria: c.naCriteria ?? null,
  procedure_guidance: c.procedureGuidance ?? null,
  tolerances: c.tolerances ?? null,
  expected_evidences: c.expectedEvidences ?? null,
  drafted_test: c.draftedTest ?? null,
  suggested_category_id: c.suggestedCategoryId ?? null,
  suggested_category_name: c.suggestedCategoryName ?? null,
});

const testingTableToRequest = (table: DraftTestingTable) => ({
  id: table.id ?? null,
  name: table.name,
  purpose: table.purpose,
  procedure_type: table.procedureType,
  attribute_references: table.attributeReferences,
  support_columns: table.supportColumns.map((c) => ({
    id: c.id ?? null,
    name: c.name,
    description: c.description,
    data_type: c.dataType,
  })),
  attribute_test_columns: table.attributeTestColumns.map((c) => ({
    id: c.id ?? null,
    name: c.name,
    pass_criteria: c.passCriteria,
    fail_criteria: c.failCriteria ?? null,
    na_criteria: c.naCriteria ?? null,
    procedure_guidance: c.procedureGuidance ?? null,
    tolerances: c.tolerances ?? null,
    expected_evidences: c.expectedEvidences ?? null,
    attribute_reference: c.attributeReference ?? null,
  })),
  // Sent back whole on every table edit: the patch replaces the table, so an
  // omitted list would read as every analytics column discarded.
  analytics_columns: table.analyticsColumns.map(analyticsColumnToRequest),
  // A saved column leaves the table only through this list; one merely
  // missing from analytics_columns is refused.
  removed_analytics_columns: table.removedAnalyticsColumns.map(
    analyticsColumnToRequest,
  ),
  testing_context: table.testingContext ?? null,
  documentation_standards: table.documentationStandards ?? null,
});

export const planPatchToRequestBody = (patch: PlanPatch) => ({
  control_name: patch.controlName ?? null,
  control_description: patch.controlDescription ?? null,
  control_type: patch.controlType ?? null,
  add_attributes: patch.addAttributes ?? null,
  update_attributes: patch.updateAttributes ?? null,
  remove_attribute_refs: patch.removeAttributeRefs ?? null,
  add_testing_tables:
    patch.addTestingTables?.map(testingTableToRequest) ?? null,
  update_testing_tables:
    patch.updateTestingTables?.map(testingTableToRequest) ?? null,
  remove_testing_table_names: patch.removeTestingTableNames ?? null,
  add_key_reports: patch.addKeyReports ?? null,
  remove_key_report_names: patch.removeKeyReportNames ?? null,
  workpaper_examples:
    patch.workpaperExamples?.map((e) => ({
      example_type: e.exampleType,
      value: e.value,
    })) ?? null,
});

// Message role
// The conversation vocabulary is not control-specific — see
// models/audit/collabMessage. Re-exported here so this file stays the flow's
// single import surface.
export {
  ClarificationRequestSchema,
  CollabMessageKindSchema,
  CollabMessageRoleSchema,
  CollabMessageSchema,
  UnappliedRequestSchema,
} from "./collabMessage";
export type {
  ClarificationRequest,
  CollabMessage,
  CollabMessageKind,
  CollabMessageRole,
  UnappliedRequest,
} from "./collabMessage";

// Session detail (from GET /sessions/{id}/detail)
export const CollabSessionDetailSchema = z
  .object({
    id: z.string().uuid(),
    phase: CollabPhaseSchema,
    current_plan: DraftPlanSchema.nullable(),
    messages: z.array(CollabMessageSchema),
    final_control_id: z.string().uuid().nullable(),
    // The control a revise session was seeded from; the backend's authoritative
    // revise-vs-generation discriminator (set => revise). Null on generation sessions.
    source_control_id: z.string().uuid().nullable().optional(),
    error_message: z.string().nullable(),
    can_undo: z.boolean().optional(),
    undo_count: z.number().optional(),
    external_ref: z.string().nullable().optional(),
    // Plan fields the session's Optro link locks (e.g. "control_name"); the
    // backend refuses edits to them, so the plan editor shows them read-only.
    optro_locked_fields: z.array(z.string()).optional(),
    // True while the revise session's seeded analytics columns still await
    // their drafted tests; the page streams draft-pending-columns to fill them.
    seeded_columns_to_draft: z.boolean().default(false),
  })
  .transform((d) => ({
    id: d.id,
    phase: d.phase,
    currentPlan: d.current_plan,
    messages: d.messages,
    finalControlId: d.final_control_id,
    sourceControlId: d.source_control_id,
    errorMessage: d.error_message,
    canUndo: d.can_undo,
    undoCount: d.undo_count,
    externalRef: d.external_ref,
    optroLockedFields: d.optro_locked_fields,
    seededColumnsToDraft: d.seeded_columns_to_draft,
  }));
export type CollabSessionDetail = z.infer<typeof CollabSessionDetailSchema>;

// Create session response
export const CreateCollabSessionResponseSchema = z.object({
  session_id: z.string().uuid(),
  external_ref: z.string().nullable().optional(),
});
export type CreateCollabSessionResponse = z.infer<
  typeof CreateCollabSessionResponseSchema
>;

// Document upload response
export const CollabUploadedDocumentSchema = z.object({
  id: z.string(),
  file_name: z.string(),
  file_type: z.string(),
  state: z.enum(["pending", "processing", "ready", "failed"]),
});
export type CollabUploadedDocument = z.infer<
  typeof CollabUploadedDocumentSchema
>;

// A document attached to a control, as returned by
// GET/POST /controls/v2/control/{id}/documents. `control_document_id` is the junction
// row id — the one the delete endpoint keys on, distinct from the underlying document_id
// that may be shared with other controls.
export const ControlDocumentInfoSchema = z.object({
  control_document_id: z.string(),
  document_id: z.string(),
  file_name: z.string(),
  file_type: z.string(),
  // Broader than the collab upload's three states (a persisted doc can be "failed" etc.);
  // kept as a plain string so the list never fails to parse on an unexpected state.
  state: z.string(),
});
export type ControlDocumentInfo = z.infer<typeof ControlDocumentInfoSchema>;

export const CollabDocumentUploadResponseSchema = z.object({
  documents: z.array(CollabUploadedDocumentSchema),
  message: z.string(),
});
export type CollabDocumentUploadResponse = z.infer<
  typeof CollabDocumentUploadResponseSchema
>;

// Phase display information for UI
export const PHASE_DISPLAY = collabPhaseDisplay({
  generating: "Creating final control",
  complete: "Control created",
});
